/* eslint-disable no-restricted-syntax -- framework calls , inject is OK */
import { Routes } from '@angular/router';
import { FeatureLpmsDataAccessModule, MatterListingType } from '@safarilaw-webapp/feature/lpms/data-access';
import { AuthGuardService } from '@safarilaw-webapp/shared/auth/store-access';
import { UserPermissionType } from '@safarilaw-webapp/shared/common-objects-models';

import { PageLeaveGuard } from '@safarilaw-webapp/shared/ui-kit/page-leave-guard';

import { PermissionGuardService } from './route-guards/permission-guard-service';

import { importProvidersFrom, inject, Injectable, provideEnvironmentInitializer } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CoManageDataAccessObject } from '@safarilaw-webapp/feature/co-manage/data-access';
import { LpmsDataAccessObject } from '@safarilaw-webapp/feature/lpms/data-access';
import { FeatureRmsDataAccessModule } from '@safarilaw-webapp/feature/rms/data-access';
import { FeatureSquirrelDataAccessModule } from '@safarilaw-webapp/feature/squirrel/data-access';
import { AppUiReduxObject } from '@safarilaw-webapp/shared/app-bootstrap/data-access';
import { AuthService } from '@safarilaw-webapp/shared/auth/store-access';
import { ActionSilenceErrorMode, NavbarItemId, SafariObject } from '@safarilaw-webapp/shared/common-objects-models';
import { ModalDialogService } from '@safarilaw-webapp/shared/dialog/store-access';
// I think NX linter is getting confused here. It thinks that the import shouldn't be allowed because we are already lazy-loading
// other /common-pages/xyz modules inside app.routing.ts, so it thinks it would be a waste to also compile the whole module staticaly in this import.
// However, these are standalone components, not one large common-pages module
// so the only real static import we have is the InitBaseComponent, not the entire common-pages module (we don't even have a common-pages module anymore)
// eslint-disable-next-line @nx/enforce-module-boundaries -- comments above
import { dataAccessMixin } from '@safarilaw-webapp/shared/redux';

import { combineLatest, Subscription } from 'rxjs';
import { ChangeSubtypeDialogComponent } from './matter/matter-edit/components/change-subtype-dialog/change-subtype-dialog.component';
import { FormScanPendingDialogComponent } from './matter/matter-edit/components/form-scan-pending-dialog/form-scan-pending-dialog.component';
import { DocumentTransferNewDialogComponent } from './matter/matter-edit/components/form/sections/lpms-document-transfer/lpms-document-transfer-new-dialog/lpms-document-transfer-new-dialog.component';
import { UpdateSubfolderDialogComponent } from './matter/matter-edit/components/update-subfolder-dialog/update-subfolder-dialog.component';
import { ChangeReviewerDialogComponent } from './matter/matter-list/components/change-reviewer-dialog/change-reviewer-dialog.component';
import { RawHtmlDialogComponent } from '@safarilaw-webapp/shared/ui-kit/html-dialog';

@Injectable()
class FeatureInitializer extends dataAccessMixin(class {}) {
  private _navigationSub: Subscription = null;
  constructor(
    modalService: ModalDialogService,
    protected _store: Store<any>,
    protected _actions: Actions<any>,
    appUiReduxObject: AppUiReduxObject,
    router: Router,
    public LpmsObject: LpmsDataAccessObject,
    public CoManageObject: CoManageDataAccessObject,
    public auth: AuthService
  ) {
    super();

    this.observeObject$(this.LpmsObject.MatterCounts, SafariObject.NOID).subscribe(o => {
      this._store.dispatch(
        appUiReduxObject.default.actions.setNavbarItemBadges({
          payload: {
            itemBadges: [
              { id: NavbarItemId.ActionAlerts.toString(), name: o.alertCount.toString() },
              { id: NavbarItemId.Unassigned.toString(), name: o.unassignedCount.toString() },
              { id: NavbarItemId.AssignedToMe.toString(), name: o.assignedToMeCount.toString() }
            ]
          }
        })
      );
    });

    modalService.registerComponent(ChangeReviewerDialogComponent.ClassId, ChangeReviewerDialogComponent);
    modalService.registerComponent(ChangeSubtypeDialogComponent.ClassId, ChangeSubtypeDialogComponent);
    modalService.registerComponent(FormScanPendingDialogComponent.ClassId, FormScanPendingDialogComponent);
    modalService.registerComponent(UpdateSubfolderDialogComponent.ClassId, UpdateSubfolderDialogComponent);
    modalService.registerComponent(DocumentTransferNewDialogComponent.ClassId, DocumentTransferNewDialogComponent);
    modalService.registerComponent(RawHtmlDialogComponent.ClassId, RawHtmlDialogComponent);
    combineLatest<[boolean, string]>([this.auth.isAuthenticated$, this.auth.companyIdClaim$]).subscribe(([isAuthenticated, companyIdClaim]) => {
      if (isAuthenticated && companyIdClaim) {
        if (this._navigationSub == null) {
          // We don't have to worry about unsubscribing here since this module exists for the duration of the app
          this._navigationSub = router.events.subscribe(evt => {
            if (evt instanceof NavigationEnd) {
              const pathsToIgnore = ['/login', '/logout', '/error'];
              if (!pathsToIgnore.includes(evt.url.toLowerCase().split('?')[0])) {
                this._dispatchCountLoad();
              }
            }
          });
        }

        this._dispatchCountLoad();

        this.loadObject(this.CoManageObject.MyCompany, SafariObject.NOID);
        this.loadObject(this.LpmsObject.LpmsCompany_Settings, SafariObject.NOID);
        this.loadObject(this.CoManageObject.MyUser, SafariObject.NOID);
      }
    });
  }

  private _dispatchCountLoad() {
    this.loadObject(this.LpmsObject.MatterCounts, SafariObject.NOID, { options: { silenceErrors: { mode: ActionSilenceErrorMode.All } } });
  }
}

export const routes: Routes = [
  {
    // Entry level to legal process
    // This one will have two children - either legal-process or settings
    // Each one of the children contains relevant components (same as legal-process and co-manage
    // top level routes used to have before merging two UIs )
    path: '',
    providers: [
      {
        provide: FeatureInitializer
      },
      importProvidersFrom(FeatureLpmsDataAccessModule, FeatureSquirrelDataAccessModule, FeatureRmsDataAccessModule),

      provideEnvironmentInitializer(() => {
        inject(FeatureInitializer);
      })
    ],
    children: [
      { path: '', redirectTo: 'legal-process', pathMatch: 'full' },
      {
        path: 'legal-process',
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./components/lpms-landing/lpms-landing.component').then(a => a.LpmsLandingComponent),
            canActivate: [AuthGuardService],
            data: { pageTitle: '', operationName: 'Lpms Landing' }
          },
          {
            path: 'intake',
            children: [
              {
                path: 'info-requests',
                pathMatch: 'full',
                loadComponent: () => import('@safarilaw-webapp/common-pages/redirector').then(a => a.RedirectorComponent),
                data: {
                  path: 'legal-process/intake/',
                  immediateRedirect: true
                }
              },
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./matter/matter-list/components/lpms-matter-list/lpms-matter-list.component').then(a => a.LpmsMatterListComponent),
                canActivate: [AuthGuardService, PermissionGuardService],
                canDeactivate: [PageLeaveGuard],
                data: {
                  documentType: MatterListingType.AllUnassigned,
                  operationName: 'Matters Unassigned - List',
                  permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
                }
              }
            ]
          },
          {
            path: 'my-dashboard',
            children: [
              {
                path: 'assigned-to-me',
                pathMatch: 'full',
                loadComponent: () => import('./matter/matter-list/components/lpms-matter-list/lpms-matter-list.component').then(a => a.LpmsMatterListComponent),
                canActivate: [AuthGuardService, PermissionGuardService],
                canDeactivate: [PageLeaveGuard],
                data: {
                  documentType: MatterListingType.AllAssignedToMe,
                  operationName: 'Matters Assigned To Me - List',
                  permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
                }
              },
              {
                path: 'recent',
                pathMatch: 'full',
                loadComponent: () => import('./matter/matter-list/components/lpms-matter-list/lpms-matter-list.component').then(a => a.LpmsMatterListComponent),
                canActivate: [AuthGuardService],
                canDeactivate: [PageLeaveGuard],
                data: { documentType: MatterListingType.AllRecent, operationName: 'Matters Recent - List' }
              },
              {
                path: 'starred',
                pathMatch: 'full',
                loadComponent: () => import('./matter/matter-list/components/lpms-matter-list/lpms-matter-list.component').then(a => a.LpmsMatterListComponent),
                canActivate: [AuthGuardService],
                canDeactivate: [PageLeaveGuard],
                data: { documentType: MatterListingType.AllStarred, operationName: 'Matters Starred - List' }
              },
              {
                path: 'info-requests',
                pathMatch: 'full',
                loadComponent: () => import('@safarilaw-webapp/common-pages/redirector').then(a => a.RedirectorComponent),
                data: {
                  path: 'legal-process/my-dashboard/assigned-to-me',
                  immediateRedirect: true
                }
              }
            ]
          },
          {
            path: 'all',
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./matter/matter-list/components/lpms-matter-list/lpms-matter-list.component').then(a => a.LpmsMatterListComponent),
                canActivate: [AuthGuardService],
                canDeactivate: [PageLeaveGuard],
                data: { documentType: MatterListingType.AllTypes, operationName: 'Matters All - List' }
              },
              {
                path: 'info-requests',
                pathMatch: 'full',
                loadComponent: () => import('@safarilaw-webapp/common-pages/redirector').then(a => a.RedirectorComponent),

                data: {
                  path: 'legal-process/all',
                  immediateRedirect: true
                }
              }
            ]
          },
          {
            path: 'action-alerts',
            pathMatch: 'full',
            loadComponent: () => import('./matter-alert/matter-alert-list/components/lpms-matter-alert-list/lpms-matter-alert-list.component').then(a => a.LpmsMatterAlertListComponent),
            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: {
              pageTitle: 'Action Alerts',
              operationName: 'Action Alerts - List',
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'invoice-templates/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./invoice-templates/invoice-template-edit/components/invoice-template-edit/invoice-template-edit.component').then(a => a.LpmsInvoiceTemplateEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Invoices', operationName: 'Invoice Template - Edit' }
          },
          {
            path: 'invoice-templates',
            pathMatch: 'full',
            loadComponent: () =>
              import('./invoice-templates/invoice-template-list/components/lpms-invoice-template-list/lpms-invoice-template-list.component').then(a => a.LpmsInvoiceTemplateListComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Invoices', operationName: 'Invoice Template - List' }
          },
          {
            path: 'subtypes',
            pathMatch: 'full',
            loadComponent: () => import('./matter-subtype/matter-subtype-list/components/matter-subtype-list/matter-subtype-list.component').then(a => a.MatterSubtypeListComponent),
            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { permissions: [UserPermissionType.Owner], pageTitle: 'Subtypes', operationName: 'Subtype - List' }
          },
          {
            path: 'subtypes/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./matter-subtype/matter-subtype-edit/components/matter-subype-edit/matter-subtype-edit.component').then(a => a.MatterSubtypeEditComponent),
            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { permissions: [UserPermissionType.Owner], operationName: 'Subtype - Edit', pageTitle: 'Subtypes' }
          },
          // Redirector example - leave these comments for now...
          // Suppose subtypes route was changed to subtypes-new. Just have the old route handled
          // by RedirectorComponent, and then pass the ABSOLUTE path in route data as shown belo
          // {
          //   path: 'subtypes-new',
          //   component: MatterSubtypeListComponent,
          //   canActivate: [AuthGuardService, PermissionGuardService],
          //   data: {
          //     permissions: [UserPermissionType.Coordinator],
          //     pageTitle: 'Subtypes',
          //     operationName: 'Subtype - List'
          //   },
          //   canDeactivate: [PageLeaveGuard],
          //   pathMatch: 'full'
          // },
          // {
          //   path: 'subtypes',
          //   component: RedirectorComponent,

          //   data: {
          //     path: 'legal-process/subtypes-new/'
          //   },
          //   pathMatch: 'full'
          // },

          // {
          //   path: 'subtypes/edit/:id',
          //   component: RedirectorComponent,
          //   data: {
          //     path: 'legal-process/subtypes-new/edit/:id'
          //   },
          //   pathMatch: 'full'
          // },
          // {
          //   path: 'subtypes-new/edit/:id',
          //   component: MatterSubtypeEditComponent,
          //   canActivate: [AuthGuardService, PermissionGuardService],
          //   data: {
          //     permissions: [UserPermissionType.Coordinator],
          //     operationName: 'Subtype - Edit',
          //     pageTitle: 'Subtypes'
          //   },
          //   canDeactivate: [PageLeaveGuard],
          //   pathMatch: 'full'
          // },

          {
            path: 'reports/export',
            pathMatch: 'full',
            loadComponent: () => import('./reports/export/components/export-report/export-report.component').then(a => a.ExportReportComponent),
            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: {
              pageTitle: 'Data Exports',
              operationName: 'Reports - Export',
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'reports/preconfigured',
            pathMatch: 'full',
            loadComponent: () => import('./reports/report-list/components/report-list/report-list.component').then(a => a.ReportListComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: {
              pageTitle: 'Reports List',
              operationName: 'Reports - List',
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'reports/preconfigured/:id',
            pathMatch: 'full',
            loadComponent: () => import('./reports/preconfigured-report-edit/components/preconfigured-report-edit/preconfigured-report-edit.component').then(a => a.PreconfiguredReportEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: {
              pageTitle: 'Report',
              operationName: 'Reports - Edit',
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'matters/create',
            pathMatch: 'full',
            loadComponent: () => import('./matter/matter-create/components/matter-create/matter-create.component').then(a => a.MatterCreateComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            runGuardsAndResolvers: 'always',
            data: {
              operationName: 'Info Request - Edit',
              hideHeader: true,
              hideNavbar: true,
              isDialog: true,
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'matter/open/:id',
            pathMatch: 'full',
            loadComponent: () => import('./matter/matter-edit/components/matter-edit-url-switcher/matter-edit-url-switcher').then(a => a.MatterEditUrlSwitcherComponent),

            canActivate: [AuthGuardService],
            canDeactivate: [PageLeaveGuard],
            runGuardsAndResolvers: 'always',
            data: { hideHeader: true, hideNavbar: true, isDialog: true }
          },
          {
            path: 'info-request/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./matter/matter-edit/components/document-request-edit/document-request-edit.component').then(a => a.DocumentRequestEditComponent),

            canActivate: [AuthGuardService],
            canDeactivate: [PageLeaveGuard],
            runGuardsAndResolvers: 'always',
            data: { operationName: 'Info Request - Edit', hideHeader: true, hideNavbar: true, isDialog: true }
          },
          {
            path: 'garnishment/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./matter/matter-edit/components/garnishment-edit/garnishment-edit.component').then(a => a.GarnishmentEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            runGuardsAndResolvers: 'always',
            data: {
              operationName: 'Garnishment - Edit',
              hideHeader: true,
              hideNavbar: true,
              isDialog: true,
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'litigation/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./matter/litigation/litigation-edit/components/litigation-edit.component').then(a => a.LitigationEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            runGuardsAndResolvers: 'always',
            data: {
              operationName: 'Litigation - Edit',
              hideHeader: true,
              hideNavbar: true,
              isDialog: true,
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'other-matter/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./matter/other-matter/other-matter-edit/components/other-matter-edit.component').then(a => a.OtherMatterEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            runGuardsAndResolvers: 'always',
            data: {
              operationName: 'OtherMatter - Edit',

              hideHeader: true,
              hideNavbar: true,
              isDialog: true,
              permissions: [UserPermissionType.Viewer, UserPermissionType.Editor, UserPermissionType.Creator, UserPermissionType.Admin, UserPermissionType.Owner]
            }
          },
          {
            path: 'support',
            pathMatch: 'full',
            loadComponent: () => import('./components/support/support/support.component').then(a => a.SupportComponent),

            // pageTitle should be blank here
            data: { pageTitle: ' ', operationName: 'Support' }
          },

          {
            path: 'library-templates',
            pathMatch: 'full',
            loadComponent: () => import('./library/library-template-list/components/library-template-list/library-template-list.component').then(a => a.LibraryTemplateListComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Library Templates', operationName: 'Library Template - List', permissions: [UserPermissionType.Admin, UserPermissionType.Owner] }
          },
          {
            path: 'library-templates/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./library/library-template-edit/components/library-template-edit/library-template-edit.component').then(a => a.LibraryTemplateEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Library Template - Edit' }
          },
          {
            path: 'library-documents',
            pathMatch: 'full',
            loadComponent: () => import('./library/library-document-list/components/library-document-list/library-document-list.component').then(a => a.LibraryDocumentListComponent),

            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Library - Internal Resources', operationName: 'Library Document - List' }
          },
          {
            path: 'library-documents/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./library/library-document-edit/components/library-document-edit/library-document-edit.component').then(a => a.LibraryDocumentEditComponent),
            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Library Document - Edit' }
          },
          {
            path: 'subfolder-groups',
            pathMatch: 'full',
            loadComponent: () => import('./subfolder-groups/subfolder-group-list/components/subfolder-group-list/subfolder-group-list.component').then(a => a.SubfolderGroupListComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Subfolder Groups', operationName: 'Subfolder Groups - List', permissions: [UserPermissionType.Admin, UserPermissionType.Owner] }
          },
          {
            path: 'subfolder-groups/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./subfolder-groups/subfolder-group-edit/components/subfolder-group-edit/subfolder-group-edit.component').then(a => a.SubfolderGroupEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Subfolder Group - Edit' }
          },
          {
            path: 'matter-list-config/edit',
            pathMatch: 'full',
            loadComponent: () => import('./matter-list-config/components/matter-list-config-edit/matter-list-config-edit.component').then(a => a.MatterListConfigEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Matter Lists', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Matter Lists - Edit' }
          },
          {
            path: 'date-picker-config/edit',
            pathMatch: 'full',
            loadComponent: () => import('./date-picker-config/components/date-picker-config-edit/date-picker-config-edit.component').then(a => a.DatePickerConfigEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Date Dropdowns', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Date Dropdowns - Edit' }
          },
          {
            path: 'system-setup',
            pathMatch: 'full',
            loadComponent: () => import('./setup/components/system-setup/system-setup.component').then(a => a.SystemSetupComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'System Setup', operationName: 'System Setup', permissions: [UserPermissionType.Admin, UserPermissionType.Owner] }
          },
          {
            path: 'company-entities/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./company-entity/company-entity-edit/components/company-entity-edit/company-entity-edit.component').then(a => a.CompanyEntityEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Company Entities', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Company Entity - Edit' }
          },
          {
            path: 'company-entities',
            pathMatch: 'full',
            loadComponent: () => import('./company-entity/company-entity-list/components/company-entity-list/company-entity-list.component').then(a => a.CompanyEntityListComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Company Entities', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Company Entity - List' }
          },
          {
            path: 'serving-parties/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./serving-party/serving-party-edit/components/serving-party-edit/serving-party-edit.component').then(a => a.ServingPartyEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Serving Parties', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Serving Party - Edit' }
          },
          {
            path: 'serving-parties',
            pathMatch: 'full',
            loadComponent: () => import('./serving-party/serving-party-list/components/serving-party-list/serving-party-list.component').then(a => a.ServingPartyListComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Serving Parties', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Serving Party - List' }
          }
        ]
      },
      {
        path: 'settings',
        children: [
          {
            path: 'intake-emails',
            loadComponent: () => import('./company/intake-email/components/inbound-email-settings-list/inbound-email-settings-list.component').then(a => a.InboundEmailSettingsListComponent),

            pathMatch: 'full',
            canActivate: [AuthGuardService, PermissionGuardService],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Intake Emails', operationName: 'Intake Email List' },
            canDeactivate: [PageLeaveGuard]
          },
          {
            path: 'registered-agent-settings',
            loadComponent: () => import('./company/registered-agent/components/registered-agent-settings/registered-agent-settings.component').then(a => a.RegisteredAgentSettingsComponent),

            pathMatch: 'full',
            canActivate: [AuthGuardService, PermissionGuardService],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Registered Agent Settings', operationName: 'Registered Agent Settings' },
            canDeactivate: [PageLeaveGuard]
          },
          {
            path: 'serveport-portals',
            loadComponent: () =>
              import('./company/serveport-portals/serveport-portals-list/components/serveport-portals-list/serveport-portals-list.component').then(a => a.ServeportPortalsListComponent),

            pathMatch: 'full',
            canActivate: [AuthGuardService, PermissionGuardService],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'ServePort Intake Portals', operationName: 'ServePort Intake Portals - List' },
            canDeactivate: [PageLeaveGuard]
          },
          {
            path: 'serveport-portals/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./company/serveport-portals/serveport-portal-edit/components/serveport-portal-edit/serveport-portal-edit.component').then(a => a.ServeportPortalEditComponent),

            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'ServePort Intake Portals', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'ServePort Intake Portals - Edit' }
          },
          {
            path: 'cousers/edit/:id',
            loadComponent: () => import('./user/co-user-edit/components/co-user-edit/co-user-edit.component').then(a => a.CoUserEditComponent),
            pathMatch: 'full',
            canActivate: [AuthGuardService, PermissionGuardService],

            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner, UserPermissionType.Self], pageTitle: 'Company Users', operationName: 'Company User - Edit' },
            canDeactivate: [PageLeaveGuard]
          },
          {
            path: 'cousers',
            loadComponent: () => import('./user/co-user-list/components/co-user-list/co-user-list.component').then(a => a.CoUserListComponent),

            pathMatch: 'full',
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Company Users', operationName: 'Company User - List' },
            canDeactivate: [PageLeaveGuard],
            canActivate: [AuthGuardService, PermissionGuardService]
          },
          {
            path: 'cooffices/edit/:id',
            loadComponent: () => import('@safarilaw-webapp/common-pages/redirector').then(a => a.RedirectorComponent),

            pathMatch: 'full',

            data: { path: 'settings/teams/edit/:id' }
          },
          {
            path: 'cooffices',
            loadComponent: () => import('@safarilaw-webapp/common-pages/redirector').then(a => a.RedirectorComponent),

            pathMatch: 'full',

            data: { path: 'settings/teams' }
          },
          {
            path: 'teams/edit/:id',
            loadComponent: () => import('./team/team-edit/components/team-edit/team-edit.component').then(a => a.TeamEditComponent),

            pathMatch: 'full',
            canActivate: [AuthGuardService, PermissionGuardService],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Team', operationName: 'Team - Edit' },
            canDeactivate: [PageLeaveGuard]
          },
          {
            path: 'teams',
            loadComponent: () => import('./team/team-list/components/team-list/team-list.component').then(a => a.TeamListComponent),

            pathMatch: 'full',
            canActivate: [AuthGuardService, PermissionGuardService],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Teams', operationName: 'Teams - List' },
            canDeactivate: [PageLeaveGuard]
          },
          {
            path: 'general/edit',
            loadComponent: () => import('./company/settings-general-edit/components/settings-general-edit/settings-general-edit.component').then(a => a.SettingsGeneralEditComponent),

            pathMatch: 'full',
            canActivate: [AuthGuardService, PermissionGuardService],
            data: { permissions: [UserPermissionType.Admin, UserPermissionType.Owner], pageTitle: 'Company Setup', operationName: 'Company General Settings - Edit' },
            canDeactivate: [PageLeaveGuard]
          },
          {
            path: 'picklist-config',
            pathMatch: 'full',
            loadComponent: () => import('./custom-picklist-config-list/custom-picklist-config-list/custom-picklist-config-list.component').then(a => a.CustomPicklistConfigListComponent),
            // component: CustomPicklistConfigListComponent,
            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Custom Picklists', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Custom Picklists - List' }
          },
          {
            path: 'picklist-config/edit/:id',
            pathMatch: 'full',
            loadComponent: () => import('./custom-picklist-config/components/custom-picklist-config-edit/custom-picklist-config-edit.component').then(a => a.CustomPicklistConfigEditComponent),
            canActivate: [AuthGuardService, PermissionGuardService],
            canDeactivate: [PageLeaveGuard],
            data: { pageTitle: 'Custom Picklists', permissions: [UserPermissionType.Admin, UserPermissionType.Owner], operationName: 'Custom Picklists - Edit' }
          }
        ]
      }
    ]
  }
];

// RouterModule.forChild([
//     {

//   ]),
